import { useMemo, useState } from "react";
import { Search, RotateCcw, AlertTriangle, Loader2, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, ReadOnlyBanner, Money } from "@/components/common";
import { FileDrop, MappingEditor, StatusPill } from "@/components/FileImport";
import DatePicker, { MonthPicker } from "@/components/DatePicker";
import PayrollStatement from "@/components/PayrollStatement";
import { api, errMsg } from "@/lib/api";
import { inr, todayISO, currentMonth, monthLabel } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FIELDS = [["code", "Employee ID", true], ["name", "Employee Name", true], ["amount", "Outstanding Advance", true]];
const AMT = /^\d*(\.\d{0,2})?$/;
const toP = (s) => {
  const n = Number(String(s ?? "").replace(/,/g, "").trim() || 0);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

function rowError(r, val) {
  const v = String(val ?? "").replace(/,/g, "").trim();
  if (!AMT.test(v) || v === ".") return "Up to 2 decimals only";
  if (toP(v) > Math.round(r.outstanding * 100)) return `Max ${inr(r.outstanding)}`;
  return "";
}

function EditTable({ rows, excluded, ded, setDed, q, disabled }) {
  const ql = q.trim().toLowerCase();
  const shown = rows.filter((r) => !ql || r.code.toLowerCase().includes(ql) || r.name.toLowerCase().includes(ql));
  const exShown = excluded.filter((r) => !ql || r.code.toLowerCase().includes(ql) || r.name.toLowerCase().includes(ql));
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto max-h-[560px]" data-testid="deduction-edit-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0 z-10">
          <tr><th className="px-4 py-2.5 text-left">Employee ID</th><th className="px-4 py-2.5 text-left">Employee Name</th><th className="px-4 py-2.5 text-right">Outstanding Advance</th><th className="px-4 py-2.5 text-right w-56">Deduction Amount (₹)</th></tr>
        </thead>
        <tbody>
          {shown.length + exShown.length === 0 && <tr><td colSpan={4} className="text-center text-slate-500 py-8">No employee matches “{q}”.</td></tr>}
          {shown.map((r) => {
            const err = rowError(r, ded[r.code]);
            return (
              <tr key={r.code} className={`border-t border-slate-100 ${err ? "bg-red-50/50" : "hover:bg-slate-50"}`} data-testid={`edit-row-${r.code}`}>
                <td className="px-4 py-2 money text-blue-800 font-semibold">{r.code}</td>
                <td className="px-4 py-2">{r.name}</td>
                <td className="px-4 py-2 text-right"><Money value={r.outstanding} /></td>
                <td className="px-4 py-2">
                  <Input inputMode="decimal" value={ded[r.code]} disabled={disabled}
                    onChange={(e) => setDed((d) => ({ ...d, [r.code]: e.target.value }))}
                    onFocus={(e) => e.target.select()}
                    onBlur={(e) => { const v = e.target.value.trim(); if (!rowError(r, v)) setDed((d) => ({ ...d, [r.code]: (toP(v) / 100).toFixed(2) })); }}
                    className={`h-9 text-right money ${err ? "border-red-500" : ""}`} data-testid={`deduction-input-${r.code}`} />
                  {err && <div className="text-[11px] text-red-700 text-right mt-0.5" data-testid={`deduction-error-${r.code}`}>{err}</div>}
                </td>
              </tr>
            );
          })}
          {exShown.map((r) => (
            <tr key={`x${r.row}`} className="border-t border-slate-100 bg-red-50" data-testid={`excluded-row-${r.row}`}>
              <td className="px-4 py-2 money text-red-800">{r.code || "—"}</td>
              <td className="px-4 py-2">{r.name || "—"}</td>
              <td className="px-4 py-2 text-right">{r.outstanding != null ? <Money value={r.outstanding} /> : "—"}</td>
              <td className="px-4 py-2 text-xs text-red-700"><StatusPill status={r.status} /> <span className="ml-1">Row {r.row}: {r.error}. Not included.</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FinalizePayroll() {
  const { can } = useAuth();
  const ro = !can("admin", "payroll");
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [ded, setDed] = useState({});
  const [q, setQ] = useState("");
  const [month, setMonth] = useState(currentMonth());
  const [date, setDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);
  const [statement, setStatement] = useState(null);

  const valid = useMemo(() => parsed?.rows.filter((r) => r.status === "ok") || [], [parsed]);
  const excluded = useMemo(() => parsed?.rows.filter((r) => r.status !== "ok") || [], [parsed]);
  const errors = valid.filter((r) => rowError(r, ded[r.code])).length;
  const totalOut = valid.reduce((a, r) => a + Math.round(r.outstanding * 100), 0);
  const totalDed = valid.reduce((a, r) => a + (rowError(r, ded[r.code]) ? 0 : toP(ded[r.code])), 0);
  const payload = { rows: valid.map((r) => ({ code: r.code, name: r.name, outstanding: r.outstanding, deduction: String(ded[r.code] || "0").replace(/,/g, "") })), payroll_period: monthLabel(month), statement_date: date, filename: parsed?.filename };

  const parse = async (f, mapping) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    if (mapping) fd.append("mapping", JSON.stringify(mapping));
    try {
      const { data } = await api.post("/payroll-statement/parse", fd);
      setFile(f); setParsed(data); setStatement(null); setQ("");
      setDed(Object.fromEntries(data.rows.filter((r) => r.status === "ok").map((r) => [r.code, "0.00"])));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const generate = async () => {
    if (errors) { toast.error(`Fix ${errors} deduction amount${errors > 1 ? "s" : ""} first`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payroll-statement/generate", payload);
      setStatement(data); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Finalize Advance" subtitle="Upload outstanding advances, decide each deduction, and generate the advance deduction statement. Ledgers change only if an administrator clicks Post to Ledgers."
        actions={parsed && !statement && <Button variant="outline" onClick={() => { setParsed(null); setFile(null); }} data-testid="payroll-reset-btn"><RotateCcw className="h-4 w-4" /> Upload another file</Button>} />
      {ro && <ReadOnlyBanner text="You have view-only access. Finalize Advance is available to Administrator and Payroll users." />}
      {statement ? (
        <PayrollStatement statement={statement} payload={payload} onEdit={() => setStatement(null)} />
      ) : !parsed ? (
        !ro && <FileDrop onFile={(f) => (/\.(xlsx|xls)$/i.test(f.name) ? parse(f) : toast.error("Please upload an Excel file (.xlsx or .xls)"))} busy={busy} testid="payroll-upload" template="payroll-statement" />
      ) : (
        <div className="space-y-4">
          <div className="text-sm text-slate-600">File: <b data-testid="payroll-filename">{parsed.filename}</b> · {parsed.total} rows · <span className="text-emerald-700">{valid.length} employees loaded</span>{excluded.length > 0 && <span className="text-red-700"> · {excluded.length} excluded (duplicate / missing data)</span>}</div>
          {parsed.missing_fields.length > 0 && <MappingEditor key={JSON.stringify(parsed.mapping)} headers={parsed.headers} mapping={parsed.mapping} fields={FIELDS} busy={busy} testid="payroll" onApply={(m) => parse(file, m)} />}
          {parsed.missing_fields.length > 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="payroll-mapping-missing"><AlertTriangle className="h-4 w-4" /> Could not find column(s): {parsed.missing_fields.join(", ")}. Map them above and click Apply.</div>
          ) : (
            <>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1 relative"><Label className="text-xs">Search employee</Label><Search className="h-4 w-4 absolute left-3 top-[34px] text-slate-400" /><Input placeholder="Employee ID or name" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 mt-1 bg-white h-10" data-testid="payroll-search-input" /></div>
                <div><Label className="text-xs">Payroll period</Label><div className="mt-1"><MonthPicker value={month} onChange={setMonth} testid="payroll-period-picker" /></div></div>
                <div><Label className="text-xs">Statement date</Label><DatePicker className="mt-1 h-10" value={date} onChange={(v) => setDate(v || todayISO())} testid="payroll-statement-date" /></div>
              </div>
              <EditTable rows={valid} excluded={excluded} ded={ded} setDed={setDed} q={q} disabled={ro} />
              <div className="bg-white rounded-xl border border-slate-200 p-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="payroll-totals">
                <div><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total outstanding advance</div><div className="money text-xl font-bold" data-testid="payroll-total-outstanding">{inr(totalOut / 100)}</div></div>
                <div><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total proposed deduction</div><div className="money text-xl font-bold text-emerald-700" data-testid="payroll-total-deduction">{inr(totalDed / 100)}</div></div>
                <div className="col-span-2 sm:col-span-1"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Remaining after deduction</div><div className="money text-xl font-bold text-blue-800" data-testid="payroll-total-balance">{inr((totalOut - totalDed) / 100)}</div></div>
              </div>
              {errors > 0 && <div className="text-sm text-red-700" data-testid="payroll-errors">{errors} deduction amount{errors > 1 ? "s need" : " needs"} fixing before the statement can be generated.</div>}
              <Button className="w-full h-14 text-base font-semibold bg-blue-800 hover:bg-blue-900" disabled={ro || busy || valid.length === 0 || errors > 0} onClick={generate} data-testid="generate-statement-btn">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileCheck2 className="h-5 w-5" />} Generate Advance Deduction Statement
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
